import React from "react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";

import {
  Row,
  Col,
} from "react-bootstrap";


const COLORS = [
  "#077834",
  "#ff4d4f",
  "#1890ff",
  "#f1c40f",
  "#8e44ad",
  "#16a085",
  "#e67e22",
  "#34495e",
];


export default function GroupDashboardCharts({
  data,
}) {

  if (!data) {
    return null;
  }


  /*
  |--------------------------------------------------------------------------
  | DISTRICT DATA
  |--------------------------------------------------------------------------
  */

  const districts =
    Array.isArray(data?.districts)
      ? data.districts
      : [];


  /*
  |--------------------------------------------------------------------------
  | DISTRICT PERCENTAGE
  |--------------------------------------------------------------------------
  */

  const districtPercentage =
    districts
      .filter(
        (district) =>
          Number(
            district.groups || 0
          ) > 0
      )
      .map(
        (district) => ({
          name:
            district.name ||
            "Unknown",

          value:
            Number(
              district.groups || 0
            ),
        })
      );


  /*
  |--------------------------------------------------------------------------
  | GROUP GENDER REPRESENTATION
  |--------------------------------------------------------------------------
  */

  const groupGenderData = [

    {
      name: "Male Groups",

      value: Number(
        data?.total_male_groups ||
          0
      ),
    },

    {
      name: "Female Groups",

      value: Number(
        data?.total_female_groups ||
          0
      ),
    },

  ];


  /*
  |--------------------------------------------------------------------------
  | FAMILY REPRESENTATION
  |--------------------------------------------------------------------------
  |
  | These keys now match Dashboard() response:
  |
  | rescued_count
  | to_be_rescued_count
  | community_count
  |--------------------------------------------------------------------------
  */

  const familyRepresentation = [

    {
      name: "Rescued",

      value: Number(
        data?.rescued_count ||
          0
      ),
    },

    {
      name: "To Be Rescued",

      value: Number(
        data?.to_be_rescued_count ||
          0
      ),
    },

    {
      name: "Community",

      value: Number(
        data?.community_count ||
          0
      ),
    },

  ];


  /*
  |--------------------------------------------------------------------------
  | MATERIAL DISTRIBUTION
  |--------------------------------------------------------------------------
  */

  const materialDistribution = [

    {
      name: "Pass Book",

      delivered: Number(
        data?.passbook_delivered ||
          0
      ),
    },

    {
      name: "Saving Box",

      delivered: Number(
        data?.saving_box_delivered ||
          0
      ),
    },

    {
      name: "Minute Book",

      delivered: Number(
        data?.minute_book_delivered ||
          0
      ),
    },

    {
      name: "Loan Ledger",

      delivered: Number(
        data?.loan_ledger_delivered ||
          0
      ),
    },

  ];


  /*
  |--------------------------------------------------------------------------
  | FAMILY TOTAL
  |--------------------------------------------------------------------------
  */

  const totalFamilyRepresentation =

    Number(
      data?.rescued_count || 0
    ) +

    Number(
      data?.to_be_rescued_count ||
        0
    ) +

    Number(
      data?.community_count || 0
    );


  /*
  |--------------------------------------------------------------------------
  | CHECK FAMILY DATA
  |--------------------------------------------------------------------------
  */

  const hasFamilyData =
    totalFamilyRepresentation > 0;


  /*
  |--------------------------------------------------------------------------
  | FORMAT NUMBER
  |--------------------------------------------------------------------------
  */

  const formatNumber = (
    number
  ) => {

    return Number(
      number || 0
    ).toLocaleString(
      "en-PK"
    );

  };


  /*
  |--------------------------------------------------------------------------
  | FORMAT PERCENT
  |--------------------------------------------------------------------------
  */

  const formatPercent = (
    number
  ) => {

    return `${Number(
      number || 0
    ).toFixed(2)}%`;

  };


  return (

    <>

      {/* ========================================================
          GROUP GENDER + MATERIAL DISTRIBUTION
      ======================================================== */}

      <Row className="mt-4">


        {/* GROUP GENDER */}

        <Col
          xl={6}
          lg={6}
          md={12}
          className="mb-4"
        >

          <div className="mc-card p-3">

            <h5 className="mb-3">

              Male / Female ACT Groups

            </h5>


            <ResponsiveContainer
              width="100%"
              height={320}
            >

              <PieChart>

                <Pie
                  data={
                    groupGenderData
                  }
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({
                    name,
                    value,
                  }) =>
                    `${name}: ${value}`
                  }
                >

                  {groupGenderData.map(
                    (
                      entry,
                      index
                    ) => (

                      <Cell
                        key={
                          `gender-${index}`
                        }
                        fill={
                          COLORS[
                            index %
                              COLORS.length
                          ]
                        }
                      />

                    )
                  )}

                </Pie>


                <Tooltip />


                <Legend />

              </PieChart>

            </ResponsiveContainer>

          </div>

        </Col>


        {/* MATERIAL DISTRIBUTION */}

        <Col
          xl={6}
          lg={6}
          md={12}
          className="mb-4"
        >

          <div className="mc-card p-3">

            <h5 className="mb-3">

              Material Distribution

            </h5>


            <ResponsiveContainer
              width="100%"
              height={320}
            >

              <BarChart
                data={
                  materialDistribution
                }
              >

                <CartesianGrid
                  strokeDasharray="3 3"
                />


                <XAxis
                  dataKey="name"
                />


                <YAxis
                  allowDecimals={
                    false
                  }
                />


                <Tooltip />


                <Bar
                  dataKey="delivered"
                  name="Delivered"
                  fill="#077834"
                />

              </BarChart>

            </ResponsiveContainer>

          </div>

        </Col>

      </Row>


      {/* ========================================================
          DISTRICT CHARTS
      ======================================================== */}

      {districts.length > 0 && (

        <Row>


          {/* DISTRICT GROUPS */}

          <Col
            xl={6}
            lg={6}
            md={12}
            className="mb-4"
          >

            <div className="mc-card p-3">

              <h5 className="mb-3">

                District / City wise ACT Groups

              </h5>


              <ResponsiveContainer
                width="100%"
                height={320}
              >

                <BarChart
                  data={
                    districts
                  }
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                  />


                  <XAxis
                    dataKey="name"
                  />


                  <YAxis
                    allowDecimals={
                      false
                    }
                  />


                  <Tooltip />


                  <Bar
                    dataKey="groups"
                    name="ACT Groups"
                    fill="#077834"
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          </Col>


          {/* DISTRICT PERCENTAGE */}

          <Col
            xl={6}
            lg={6}
            md={12}
            className="mb-4"
          >

            <div className="mc-card p-3">

              <h5 className="mb-3">

                District / City Percentage Share

              </h5>


              <ResponsiveContainer
                width="100%"
                height={320}
              >

                <PieChart>

                  <Pie
                    data={
                      districtPercentage
                    }
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >

                    {districtPercentage.map(
                      (
                        entry,
                        index
                      ) => (

                        <Cell
                          key={
                            `district-${index}`
                          }
                          fill={
                            COLORS[
                              index %
                                COLORS.length
                            ]
                          }
                        />

                      )
                    )}

                  </Pie>


                  <Tooltip />


                  <Legend />

                </PieChart>

              </ResponsiveContainer>

            </div>

          </Col>

        </Row>

      )}


      {/* ========================================================
          FAMILY REPRESENTATION
      ======================================================== */}

      {hasFamilyData && (

        <Row>


          <Col
            xl={6}
            lg={6}
            md={12}
            className="mb-4"
          >

            <div className="mc-card p-3">

              <h5 className="mb-3">

                Family Representation

              </h5>


              <ResponsiveContainer
                width="100%"
                height={320}
              >

                <PieChart>

                  <Pie
                    data={
                      familyRepresentation
                    }
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({
                      name,
                      value,
                    }) =>
                      `${name}: ${value}`
                    }
                  >

                    {familyRepresentation.map(
                      (
                        entry,
                        index
                      ) => (

                        <Cell
                          key={
                            `family-${index}`
                          }
                          fill={
                            COLORS[
                              index %
                                COLORS.length
                            ]
                          }
                        />

                      )
                    )}

                  </Pie>


                  <Tooltip />


                  <Legend />

                </PieChart>

              </ResponsiveContainer>

            </div>

          </Col>


          {/* FAMILY COUNTS BAR */}

          <Col
            xl={6}
            lg={6}
            md={12}
            className="mb-4"
          >

            <div className="mc-card p-3">

              <h5 className="mb-3">

                Family Category Comparison

              </h5>


              <ResponsiveContainer
                width="100%"
                height={320}
              >

                <BarChart
                  data={
                    familyRepresentation
                  }
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                  />


                  <XAxis
                    dataKey="name"
                  />


                  <YAxis
                    allowDecimals={
                      false
                    }
                  />


                  <Tooltip />


                  <Bar
                    dataKey="value"
                    name="Families"
                    fill="#077834"
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          </Col>

        </Row>

      )}


      {/* ========================================================
          FAMILY REPRESENTATION INDICATORS
      ======================================================== */}

      <Row>

        <Col
          xl={12}
          className="mb-4"
        >

          <div
            className="mc-card p-4"
            style={{
              border:
                "2px solid #dc3545",
            }}
          >

            <h4
              style={{
                color:
                  "#dc3545",
              }}
              className="mb-4"
            >

              Family Representation
              (Critical Indicators)

            </h4>


            <Row>


              {/* RESCUED */}

              <Col
                md={4}
                className="mb-3"
              >

                <div>

                  <p className="mb-1">

                    <strong>
                      Total Family Representation
                    </strong>

                  </p>

                  <h5>

                    {formatNumber(
                      totalFamilyRepresentation
                    )}

                  </h5>

                </div>


                <hr />


                <div>

                  <p className="mb-1">

                    Rescued Family Representation

                  </p>

                  <h5>

                    {formatNumber(
                      data?.rescued_count
                    )}

                  </h5>

                </div>


                <div className="mt-3">

                  <p className="mb-1">

                    % Rescued Families

                  </p>

                  <h5>

                    {formatPercent(
                      data?.rescued_percent
                    )}

                  </h5>

                </div>

              </Col>


              {/* COMMUNITY */}

              <Col
                md={4}
                className="mb-3"
              >

                <div>

                  <p className="mb-1">

                    Community Family Representation

                  </p>

                  <h5>

                    {formatNumber(
                      data?.community_count
                    )}

                  </h5>

                </div>


                <hr />


                <div>

                  <p className="mb-1">

                    % Community Families

                  </p>

                  <h5>

                    {formatPercent(
                      data?.community_percent
                    )}

                  </h5>

                </div>

              </Col>


              {/* TO BE RESCUED */}

              <Col
                md={4}
                className="mb-3"
              >

                <div>

                  <p className="mb-1">

                    To-Be-Rescued Family Representation

                  </p>

                  <h5>

                    {formatNumber(
                      data?.to_be_rescued_count
                    )}

                  </h5>

                </div>


                <hr />


                <div>

                  <p className="mb-1">

                    % To-Be-Rescued Families

                  </p>

                  <h5>

                    {formatPercent(
                      data?.to_be_rescued_percent
                    )}

                  </h5>

                </div>

              </Col>

            </Row>

          </div>

        </Col>

      </Row>

    </>

  );
}